import type { Todo } from "../../domain/entities.ts";

export interface TaskUndoSnapshot {
  rootId?: string;
  checkpoint?: boolean;
  id: string;
  completed: boolean;
  completedAt: number | null;
  pinned: boolean;
  archivedAt: number | null;
  deleted: boolean;
  categoryId: string | null;
  position: number;
  dueDate: string | null;
  startDate: string | null;
  startAt: string | null;
  endAt: string | null;
  wallStartAt: string | null;
  wallEndAt: string | null;
  estimatedMinutes: number | null;
}

export type TaskUndoField = Exclude<keyof TaskUndoSnapshot, "id" | "rootId">;

export interface TaskUndoCapabilities {
  rootOf?(id: string): string | undefined;
  change(id: string, rootId: string, update: (todo: Todo) => void): Promise<unknown>;
  offer(message: string, undo: () => void | Promise<void>): void;
}

export function taskUndoSnapshot(todo: Todo): TaskUndoSnapshot {
  return {
    id: todo.id,
    ...(todo.rootId ? { rootId: todo.rootId } : {}),
    checkpoint: !!todo.checkpoint,
    completed: todo.completed,
    completedAt: todo.completedAt ?? null,
    pinned: !!todo.pinned,
    archivedAt: todo.archivedAt ?? null,
    deleted: todo.deleted,
    categoryId: todo.categoryId,
    position: todo.position,
    dueDate: todo.dueDate,
    startDate: todo.startDate ?? null,
    startAt: todo.startAt ?? null,
    endAt: todo.endAt ?? null,
    wallStartAt: todo.wallStartAt ?? null,
    wallEndAt: todo.wallEndAt ?? null,
    estimatedMinutes: todo.estimatedMinutes ?? null,
  };
}

export function createTaskUndoController(capabilities: TaskUndoCapabilities) {
  function offer(message: string, rootId: string, before: TaskUndoSnapshot[], fields: TaskUndoField[], generatedIds: string[] = []): void {
    capabilities.offer(message, async () => {
      await Promise.all(before.map(snapshot => capabilities.change(snapshot.id, snapshot.rootId || rootId, todo => {
        fields.forEach(field => { (todo as unknown as Record<string, unknown>)[field] = snapshot[field]; });
      })));
      const restoredIds = new Set(before.map(snapshot => snapshot.id));
      await Promise.all(generatedIds.filter(id => !restoredIds.has(id)).map(id => capabilities.change(id, capabilities.rootOf?.(id) || rootId, todo => { todo.deleted = true; })));
    });
  }

  return { offer };
}
